/* eslint-disable functional/no-classes -- Error boundaries require class components in React */
import React, { Component, ReactNode } from "react";
import { Platform } from "react-native";

import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/**
 * Props for the ErrorBoundary component
 */
interface Props {
  /** Child components to wrap with error boundary protection */
  children: ReactNode;
  /** Optional custom fallback UI to display when an error occurs */
  fallback?: ReactNode;
  /** Callback invoked when an error is caught with error details */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

/**
 * State for the ErrorBoundary component
 */
interface State {
  /** Whether an error has been caught by the boundary */
  hasError: boolean;
  /** The caught error object, if any */
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error);
    console.error("Error Info:", errorInfo);

    this.props.onError?.(error, errorInfo);
  }

  /**
   * Render method that returns the children when no error, or fallback/error UI when an error is caught.
   * @remarks
   * React Error Boundaries require returning different content based on error state.
   * This is an inherent pattern in React's error boundary API where render() must return
   * either children (ReactNode) or error UI (JSX.Element) depending on hasError state.
   */

  render(): ReactNode {
    if (!this.state.hasError) {
      return <>{this.props.children}</>;
    }

    if (this.props.fallback) {
      return <>{this.props.fallback}</>;
    }

    // Default fallback UI
    return (
      <Stack className="flex-1 items-center justify-center gap-2 p-4">
        <Text variant="unstyled" className="text-center text-content-muted">
          {Platform.OS === "web"
            ? "Something went wrong. Please refresh the page."
            : "Something went wrong. Please try again."}
        </Text>
        {this.state.error && (
          <Text variant="caption" className="text-status-error">
            {this.state.error.message}
          </Text>
        )}
      </Stack>
    );
  }
}

export default ErrorBoundary;

/* eslint-enable functional/no-classes -- Error boundaries require class components in React */
